import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import { getLensContent } from "../../../../services/lensService";
import { logError } from "../../../../utils/error";
import { CONVERSATION_LENS } from "../../../../utils/terminology";
import { useAuth } from "../../../auth/AuthContext";
import { Lens } from "../../types";
import { extractDescriptionFromMarkdown } from "../../utils/markdown";

interface LensDetailsDialogProps {
  open: boolean;
  lens: Lens | null;
  onClose: () => void;
}

export const LensDetailsDialog: React.FC<LensDetailsDialogProps> = ({
  open,
  lens,
  onClose
}) => {
  const { tenantId } = useAuth();
  const [lensContent, setLensContent] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dynamicDescription, setDynamicDescription] = useState<string | null>(null);

  const fetchLensContent = useCallback(async () => {
    if (!lens || !tenantId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const content = await getLensContent(tenantId, lens.fullPath);
      setLensContent(content);

      // Extract description from the markdown content
      const extractedDescription = extractDescriptionFromMarkdown(content);
      setDynamicDescription(extractedDescription);
    } catch (err) {
      logError("Failed to fetch lens content", err);
      setError(err instanceof Error ? err.message : "Failed to fetch lens content");
    } finally {
      setLoading(false);
    }
  }, [lens, tenantId]);

  useEffect(() => {
    if (open && lens && tenantId) {
      fetchLensContent();
    } else {
      // Reset state when dialog closes or lens changes
      setLensContent("");
      setError(null);
      setDynamicDescription(null);
    }
  }, [open, lens, tenantId, fetchLensContent]);

  if (!lens) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          pb: 2
        }}
      >
        {CONVERSATION_LENS} Configuration
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box mb={3} mt={3}>
          <Typography gutterBottom>
            <strong>{CONVERSATION_LENS} Name:</strong> {lens.name}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            <strong>Description:</strong> {dynamicDescription || lens.description || "No description provided"}
          </Typography>
        </Box>

        <Box>
          <Typography gutterBottom>
            <strong>{CONVERSATION_LENS} Configuration:</strong>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper
            sx={{
              p: 2,
              backgroundColor: "grey.50",
              fontFamily: "monospace",
              fontSize: "0.875rem",
              whiteSpace: "pre-wrap",
              overflow: "auto",
              maxHeight: "60vh",
              minHeight: "200px",
              display: "flex",
              alignItems: loading ? "center" : "flex-start",
              justifyContent: loading ? "center" : "flex-start"
            }}
          >
            {loading ? (
              <Box display="flex" alignItems="center" gap={2}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading lens content...
                </Typography>
              </Box>
            ) : error ? (
              <Typography color="error">
                Failed to load lens content. Path: {lens.fullPath}
              </Typography>
            ) : lensContent ? (
              lensContent
            ) : (
              <Typography color="text.secondary">
                Path: {lens.fullPath}
                {"\n\n"}No content available
              </Typography>
            )}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: 1,
          borderColor: "divider",
          pt: 2
        }}
      >
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
